// Task AND UploadedDocument default icon
import FileText from "~icons/ph/file-text"
// UploadedDocuments
import ImageSquare from "~icons/ph/image-square-duotone"
import FilePdf from "~icons/ph/file-pdf-duotone"
// Tasks
import Upload from "~icons/ph/upload"
import CarProfile from "~icons/ph/car-profile"
import ClipboardText from "~icons/ph/clipboard-text"
import Bank from "~icons/ph/bank"
import CalendarDots from "~icons/ph/calendar"
import IdentificationCard from "~icons/ph/identification-card"
import Wrench from "~icons/ph/wrench"
import Taxi from "~icons/ph/taxi"
import Cake from "~icons/ph/cake"
import AddressBook from "~icons/ph/address-book-tabs-light"
import EnvelopeSimple from "~icons/ph/envelope-simple"
import NumPad from "~icons/ph/numpad"
import Signpost from "~icons/ph/signpost"
import PenNib from "~icons/ph/pen-nib"
import Scroll from "~icons/ph/scroll"
import AirplaneTilt from "~icons/ph/airplane-tilt"
import Warning from "../icons/Warning.svelte"
import Sparkle from "../icons/Sparkle.svelte"

const TASK_ICONS = {
  "file-text": FileText,
  "upload": Upload,
  "clipboard-text": ClipboardText,
  "car-profile": CarProfile,
  "bank": Bank,
  "calendar-dots": CalendarDots,
  "identification-card": IdentificationCard,
  "wrench": Wrench,
  "taxi": Taxi,
  "cake": Cake,
  "address-book": AddressBook,
  "envelope-simple": EnvelopeSimple,
  "numpad": NumPad,
  "signpost": Signpost,
  "pen-nib": PenNib,
  "scroll": Scroll,
  "airplane-tilt": AirplaneTilt,
  "warning": Warning,
  "sparkle": Sparkle,
} as const;

// Define more `subtitle` colors as required
const STATUS_CLASSES = {
  incorrect: {border: "border-error", subtitle: "text-error"},
  not_started: {border: "border-warning", subtitle: "text-gray-600"},
  optional: {border: "border-info", subtitle: "text-gray-600"},
  deferred: {border: "border-gray-200", subtitle: "text-gray-600"},
  pending_review: {border: "border-success", subtitle: "text-gray-600"},
  completed: {border: "border-success", subtitle: "text-gray-600"},
  unavailable: {border: "border-success", subtitle: "text-gray-600"}
}

const TODO_STATUS = ["incorrect", "not_started"] as const
const DONE_STATUS = ["unavailable", "completed", "pending_review"] as const
const ALL_TASK_STATUS = [...TODO_STATUS, "optional", "deferred", ...DONE_STATUS] as const

export type TaskLineTuple = [string, Record<string, unknown>];

export type TaskData = {
  path : string;
  icon : keyof typeof TASK_ICONS;
  status : (typeof ALL_TASK_STATUS)[number];
  title : string;
  subtitle: string | null;
  deferred_reason: string;
  add_more : boolean;
  ready : boolean;
  document_requested : string | null;
  documents_uploaded : UploadedDocument[] | null;
  task_line?: TaskLineTuple | null;
}

export type UploadedDocumentData = {
  filename : string;
  created_at : string; // Full datetime string
}

export class Task {
  path : string;
  icon_name : keyof typeof TASK_ICONS;
  status : (typeof ALL_TASK_STATUS)[number];
  title : string;
  #subtitle: string | null | undefined;
  deferredReason: string | null | undefined;
  #buttonLabel : string | null | undefined;
  #addMore : boolean;
  #priority : string | null | undefined;
  ready : boolean;
  docRequested : string | null | undefined;
  docsUploaded : UploadedDocument[];
  taskLine: TaskLineTuple | null;

  constructor(attrs){
    this.path = attrs.path
    this.icon_name = attrs.icon
    this.status = attrs.status
    this.title = attrs.title
    this.#subtitle = attrs.subtitle
    this.deferredReason = attrs.deferred_reason
    this.#buttonLabel = attrs.button_label
    this.#addMore = attrs.add_more || false
    this.#priority = attrs.priority
    this.ready = attrs.ready
    this.docRequested = attrs.document_requested || null
    this.docsUploaded = UploadedDocument.sortedFromArray(attrs.documents_uploaded || [])
    this.taskLine = attrs.task_line
  }

  get subtitle(){
    if (this.#subtitle) {
      return this.#subtitle;
    }
    if (this.docRequest) {
      if (this.incorrect) { return "Please try again; see email for details" }
      if (this.optional) { return "If applicable" }
    }
  }

  get icon(){ return TASK_ICONS[this.icon_name] }
  get borderClass(){ return STATUS_CLASSES[this.status].border }
  get subtitleClass() { return STATUS_CLASSES[this.status].subtitle }
  get buttonLabel(){ return this.#buttonLabel || (this.incorrect ? "Update" : "Respond") }

  // Boolean accessors. Wish we could use ? in method names.
  get addMore(){ return this.#addMore || this.unavailable }
  get incorrect(){ return this.status == "incorrect" }
  get optional(){ return this.status == "optional" }
  get deferred(){ return this.status == "deferred" }
  get unavailable(){ return this.status == "unavailable" }
  get todo(){ return (TODO_STATUS as readonly string[]).includes(this.status) }
  get done(){ return (DONE_STATUS as readonly string[]).includes(this.status) }
  get loading(){ return !this.done && !this.ready }
  get docRequest(){ return !!this.docRequested }
  // Probably will have more robust & complete handling of high priority later.
  // If we do, you're welcome to refactor bigProblem and the UI that uses it.
  get bigProblem(){ return this.#priority == "high" && this.incorrect }


  // Tasks are expected to be priority-sorted on the server already.
  static fromArray(tasksData : TaskData[]) : Task[] {
    return tasksData.map(task => new Task(task))
  }
}

export class UploadedDocument {
  filename;
  created;
  isNew;

  constructor(attrs){
    this.filename = attrs.filename
    this.created = new Date(attrs.created_at)
    this.isNew = false
  }

  // Rails has access to more powerful mime type inference, but for the moment we
  // don't need anything more complicated than this.
  get icon(){
    if(/\.pdf\s*$/i.test(this.filename)) return FilePdf;
    if(/\.(jpe?g|png|heic|gif|bmp|tiff?)\s*$/i.test(this.filename)) return ImageSquare;
    return FileText;
  }

  sameAs(doc){
    if(!doc) return false;
    return this.filename == doc.filename && this.created.getTime() == doc.created.getTime()
  }

  // Most recent first. If you change this, change markNew
  static sortedFromArray(docs) : UploadedDocument[] {
    docs = docs.map(doc => new UploadedDocument(doc))
    docs.sort((a, b)=>b.created.getTime() - a.created.getTime())
    return docs
  }

  // Expects sorted as from sortedFromArray
  static markNew(prev, next) {
    if(!next?.length) return false;
    let anyNew = false
    const last = prev?.[0]
    for(const doc of next) {
      if(doc.sameAs(last)) break;
      anyNew = doc.isNew = true
    }
    return anyNew
  }
}
